import React, { useEffect, useState } from "react";
import { Check, Crown, ShieldCheck, HeartHandshake } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Plans() {
  const { user, setUser } = useAuth();
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState("");

  useEffect(() => { api.get("/plans").then((r) => setPlans(r.data)).catch(() => {}); }, []);

  const choose = async (id) => {
    if (id !== "free") {
      setBusy(id);
      try {
        const { data } = await api.post("/payments/checkout", {
          lookup_key: id === "family" ? "family_monthly" : "verified_monthly",
          origin_url: window.location.origin,
        });
        window.location.href = data.checkout_url;
      } catch (e) { toast.error(apiError(e)); setBusy(""); }
      return;
    }
    setBusy(id);
    try {
      const { data } = await api.post("/subscribe", { plan: id });
      setUser(data);
      toast.success("You're on Connect Basic");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const ICON = { free: HeartHandshake, verified: ShieldCheck, family: Crown };

  return (
    <div className="max-w-5xl mx-auto">
      <h1 className="font-head text-3xl sm:text-4xl font-bold text-forest-900">Plans</h1>
      <p className="text-lg text-forest-700 mt-2 mb-2 max-w-2xl">Basic human connection is always free. Upgrade for trust, visibility and peace of mind for the people you love.</p>
      <p className="text-sm text-forest-500 mb-8 font-medium bg-gold-light border border-gold rounded-full inline-block px-4 py-1.5">Secure checkout by Stripe (test mode). Use card 4242 4242 4242 4242, any future date & CVC.</p>

      <div className="grid md:grid-cols-3 gap-6" data-testid="plans-grid">
        {plans.map((p) => {
          const Icon = ICON[p.id] || HeartHandshake;
          const current = (user?.plan || "free") === p.id;
          return (
            <div key={p.id} data-testid={`plan-${p.id}`}
              className={`bg-white rounded-2xl border-2 p-6 sm:p-8 flex flex-col ${p.highlight ? "border-terracotta shadow-md relative" : "border-border shadow-sm"}`}>
              {p.highlight && <span className="absolute -top-3 left-6 bg-terracotta text-white text-xs font-bold px-3 py-1 rounded-full">Most popular</span>}
              <div className="w-12 h-12 rounded-xl bg-cream border-2 border-border flex items-center justify-center mb-4">
                <Icon size={26} strokeWidth={2.5} className="text-terracotta" />
              </div>
              <h3 className="font-head text-2xl font-bold text-forest-900">{p.name}</h3>
              <p className="text-forest-500 font-medium">{p.tagline}</p>
              <div className="mt-4 mb-5">
                <span className="font-head text-4xl font-extrabold text-forest-900">{p.price === 0 ? "Free" : `$${p.price}`}</span>
                {p.price !== 0 && <span className="text-forest-500 font-semibold">/{p.period}</span>}
              </div>
              <ul className="space-y-2.5 mb-6 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-forest-700"><Check size={20} strokeWidth={2.6} className="text-forest-700 shrink-0 mt-0.5" /> {f}</li>
                ))}
              </ul>
              <button onClick={() => choose(p.id)} disabled={current || busy === p.id} data-testid={`choose-${p.id}`}
                className={`min-h-[56px] rounded-full font-bold text-lg transition-transform active:scale-95 ${current ? "bg-forest-700 text-white cursor-default" : p.highlight ? "bg-terracotta text-white hover:bg-terracotta-dark" : "bg-white border-2 border-forest-900 text-forest-900 hover:bg-forest-900 hover:text-white"}`}>
                {current ? "Your current plan" : busy === p.id ? "Updating…" : p.id === "free" ? "Switch to Basic" : "Choose plan"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
